import { Link, createFileRoute } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { getHours } from '@mkcore/portal/schema';
import { Badge, Button, Card, ErrorState, Skeleton, TBody, THead, Table, Td, Th, Tr } from '@mkcore/ui';
import { call } from '@/lib/api';
import { formatDate, formatDuration, formatMoney } from '@/lib/format';

/** What the client sees of the hours of one engagement (CO-1352): use, billed hours and time entries. */
export const Route = createFileRoute('/portal/_client/hours/$id')({
  component: HoursOf,
});

function Bar({ used, total, label }: { used: number; total: number; label: string }) {
  const pct = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;
  return (
    <div className="grid gap-1">
      <div className="h-2 overflow-hidden rounded-full bg-sunken" role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label}>
        <div className={`h-full ${pct >= 100 ? 'bg-danger' : pct >= 90 ? 'bg-warning' : 'bg-gold'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function HoursOf() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const q = useInfiniteQuery({
    queryKey: ['portal', 'hours', id],
    queryFn: ({ pageParam }) => call(getHours, { id, ...(pageParam && { cursor: pageParam }) }),
    initialPageParam: '',
    getNextPageParam: (last) => last.entries?.nextCursor ?? undefined,
  });
  if (q.isError) return <ErrorState title={t('portal.hours.notFound')} />;
  if (!q.data) return <Skeleton className="h-64" />;
  const first = q.data.pages[0]!;
  const entries = q.data.pages.flatMap((p) => p.entries?.items ?? []);
  return (
    <>
      <Link to="/portal/hours" className="flex w-fit items-center gap-1 py-1 text-sm text-fg-2 hover:underline">
        <ArrowLeft className="size-4" aria-hidden /> {t('portal.modules.hours')}
      </Link>
      <h1 className="text-2xl font-bold">{first.engagement.name}</h1>
      {first.retainer && (
        <Card className="grid gap-3 p-4">
          <h2 className="text-lg font-bold">{t('portal.hours.retainer', { hours: formatDuration(first.retainer.allowanceSeconds) })}</h2>
          <ul className="grid gap-3">
            {first.retainer.items.map((p) => (
              <li key={p.periodFrom} className="grid gap-1">
                <div className="flex flex-wrap justify-between gap-2 text-sm">
                  <span className="font-bold">
                    {formatDate(p.periodFrom)} – {formatDate(p.periodTo)} {!p.complete && <Badge tone="info">{t('portal.hours.current')}</Badge>}
                  </span>
                  <span className="tabular">
                    {t('portal.hours.used', { used: formatDuration(p.usedSeconds), total: formatDuration(p.allowanceSeconds + p.carriedInSeconds) })}
                    {p.overageSeconds > 0 && <span className="text-danger"> · {t('portal.hours.overage', { hours: formatDuration(p.overageSeconds) })}</span>}
                  </span>
                </div>
                <Bar used={p.usedSeconds} total={p.allowanceSeconds + p.carriedInSeconds} label={t('portal.hours.useOf', { from: formatDate(p.periodFrom) })} />
              </li>
            ))}
          </ul>
        </Card>
      )}
      {first.budget && (
        <Card className="grid gap-3 p-4">
          <h2 className="text-lg font-bold">{t('portal.hours.budget')}</h2>
          {first.budget.budgetSeconds !== null && (
            <div className="grid gap-1">
              <p className="text-sm tabular">{t('portal.hours.used', { used: formatDuration(first.budget.spentSeconds), total: formatDuration(first.budget.budgetSeconds) })}</p>
              <Bar used={first.budget.spentSeconds} total={first.budget.budgetSeconds} label={t('portal.hours.budget')} />
            </div>
          )}
          {first.budget.budgetCents !== null && (
            <div className="grid gap-1">
              <p className="text-sm tabular">{t('portal.hours.used', { used: formatMoney(first.budget.spentCents, first.budget.currency), total: formatMoney(first.budget.budgetCents, first.budget.currency) })}</p>
              <Bar used={first.budget.spentCents} total={first.budget.budgetCents} label={t('portal.hours.budgetAmount')} />
            </div>
          )}
        </Card>
      )}
      {first.billed && (
        <Card className="grid gap-2 p-4">
          <h2 className="text-lg font-bold">{t('portal.hours.billed')}</h2>
          {!first.billed.length ? (
            <p className="text-sm text-fg-2">{t('portal.hours.nothingBilled')}</p>
          ) : (
            <ul className="grid gap-1 text-sm">
              {first.billed.map((b) => (
                <li key={b.month} className="flex justify-between gap-2 tabular">
                  <span>{b.month}</span>
                  <span>{formatDuration(b.seconds)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
      {first.entries && (
        <Card className="grid gap-2 p-4">
          <h2 className="text-lg font-bold">{t('portal.hours.entries')}</h2>
          {!entries.length ? (
            <p className="text-sm text-fg-2">{t('portal.hours.noEntries')}</p>
          ) : (
            <Table aria-label={t('portal.hours.entries')}>
              <THead>
                <Tr>
                  <Th>{t('portal.hours.date')}</Th>
                  <Th>{t('portal.hours.description')}</Th>
                  <Th className="hidden sm:table-cell">{t('portal.hours.person')}</Th>
                  <Th className="text-right">{t('portal.hours.time')}</Th>
                </Tr>
              </THead>
              <TBody>
                {entries.map((e, i) => (
                  <Tr key={`${e.date}-${i}`}>
                    <Td className="whitespace-nowrap tabular">{formatDate(e.date)}</Td>
                    <Td>
                      {e.description}
                      {e.project && <span className="block text-xs text-fg-3">{e.project}</span>}
                    </Td>
                    <Td className="hidden sm:table-cell">{e.person}</Td>
                    <Td className="text-right tabular">
                      {formatDuration(e.seconds)}
                      {e.billed && <span className="block text-xs text-fg-3">{t('portal.hours.isBilled')}</span>}
                    </Td>
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
          {q.hasNextPage && (
            <Button variant="secondary" size="sm" className="w-fit" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
              {t('portal.hours.more')}
            </Button>
          )}
        </Card>
      )}
    </>
  );
}
